import React from 'react';
import { CheckIcon, MonitorSmartphoneIcon } from 'lucide-react';
import { trackAddToCart } from '../utils/metaPixel';
import { getHotmartUrl } from '../utils/utmParams';
export function MechanismReveal() {
  return <section className="bg-cream py-12 md:py-16 px-5" data-id="element-407">
      <div className="max-w-5xl mx-auto" data-id="element-408">
        {/* Eyebrow */}
        <p className="font-sans text-sm font-semibold uppercase tracking-widest text-burnt text-center mb-4" data-id="element-409">
          Introducing The Solution
        </p>

        {/* Headline */}
        <h2 className="font-serif text-forest text-3xl md:text-4xl font-bold text-center mb-14 max-w-3xl mx-auto leading-tight" data-id="element-410">
          The 365 Garden Shield: Your Complete All-Season Garden Protection
          System
        </h2>

        {/* Two Column Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center" data-id="element-411">
          {/* LEFT — Product Image */}
          <div className="flex justify-center" data-id="element-412">
            <div className="relative max-w-xs mx-auto" data-id="element-413">
              {/* Digital Edition Seal */}
              <div className="absolute -top-3 -right-3 z-20 bg-burnt text-white rounded-full w-20 h-20 flex flex-col items-center justify-center shadow-lg border-4 border-cream transform rotate-12" data-id="element-414">
                <MonitorSmartphoneIcon className="w-4 h-4 mb-0.5" data-id="element-415" />
                <span className="text-[9px] font-bold uppercase leading-tight text-center font-sans" data-id="element-416">
                  Digital
                  <br data-id="element-417" />
                  Edition
                </span>
              </div>

              {/* Product Image */}
              <div className="rounded-2xl overflow-hidden shadow-2xl" data-id="element-418">
                <img src="https://cdn.magicpatterns.com/uploads/cAcbYeq1SW7JTsSvxasWHV/3cf7d2f6-0245-4457-a4ec-5e72d308ba3a.png" alt="The 365 Garden Shield — All-Season Protection System for Every Backyard" className="w-full h-auto" data-id="element-419" />
              </div>

              {/* Subtle reflection */}
              <div className="absolute -bottom-4 left-1/2 -translate-x-1/2 w-3/4 h-4 bg-black/10 rounded-full blur-lg" data-id="element-420" />

              {/* Digital badge below */}
              <p className="text-center mt-6 font-sans text-xs text-gray-400 flex items-center justify-center gap-1.5" data-id="element-421">
                <MonitorSmartphoneIcon className="w-3.5 h-3.5" data-id="element-422" />
                Instant digital download — read on any device
              </p>
            </div>
          </div>

          {/* RIGHT — Bullet List */}
          <div className="space-y-5" data-id="element-423">
            <div className="flex items-start gap-3" data-id="element-424">
              <CheckIcon className="w-6 h-6 text-forest flex-shrink-0 mt-0.5" strokeWidth={2.5} data-id="element-425" />
              <p className="font-sans text-gray-800 leading-relaxed" data-id="element-426">
                <span className="font-bold text-forest-dark" data-id="element-427">
                  The USDA Hardiness Zone Map
                </span>{' '}
                — Know exactly what YOUR garden needs by zip code
              </p>
            </div>

            <div className="flex items-start gap-3" data-id="element-428">
              <CheckIcon className="w-6 h-6 text-forest flex-shrink-0 mt-0.5" strokeWidth={2.5} data-id="element-429" />
              <p className="font-sans text-gray-800 leading-relaxed" data-id="element-430">
                <span className="font-bold text-forest-dark" data-id="element-431">
                  The Frost-Night Emergency Checklist
                </span>{' '}
                — What to do at 10PM when temps drop fast
              </p>
            </div>

            <div className="flex items-start gap-3" data-id="element-432">
              <CheckIcon className="w-6 h-6 text-forest flex-shrink-0 mt-0.5" strokeWidth={2.5} data-id="element-433" />
              <p className="font-sans text-gray-800 leading-relaxed" data-id="element-434">
                <span className="font-bold text-forest-dark" data-id="element-435">
                  The Summer Scorch Survival Protocol
                </span>{' '}
                — Save plants during 100°F+ heat waves
              </p>
            </div>

            <div className="flex items-start gap-3" data-id="element-436">
              <CheckIcon className="w-6 h-6 text-forest flex-shrink-0 mt-0.5" strokeWidth={2.5} data-id="element-437" />
              <p className="font-sans text-gray-800 leading-relaxed" data-id="element-438">
                <span className="font-bold text-forest-dark" data-id="element-439">
                  The Storm-Ready Garden Blueprint
                </span>{' '}
                — Secure your beds before the wind hits
              </p>
            </div>

            <div className="flex items-start gap-3" data-id="element-440">
              <CheckIcon className="w-6 h-6 text-forest flex-shrink-0 mt-0.5" strokeWidth={2.5} data-id="element-441" />
              <p className="font-sans text-gray-800 leading-relaxed" data-id="element-442">
                <span className="font-bold text-forest-dark" data-id="element-443">
                  The 24-Hour Recovery Plan
                </span>{' '}
                — Revive plants the morning after any weather disaster
              </p>
            </div>

            <div className="flex items-start gap-3" data-id="element-444">
              <CheckIcon className="w-6 h-6 text-forest flex-shrink-0 mt-0.5" strokeWidth={2.5} data-id="element-445" />
              <p className="font-sans text-gray-800 leading-relaxed" data-id="element-446">
                <span className="font-bold text-forest-dark" data-id="element-447">
                  The "Cloud Cover" Hack
                </span>{' '}
                — Gain 5–10°F of protection with $3 fabric
              </p>
            </div>

            <div className="flex items-start gap-3" data-id="element-448">
              <CheckIcon className="w-6 h-6 text-forest flex-shrink-0 mt-0.5" strokeWidth={2.5} data-id="element-449" />
              <p className="font-sans text-gray-800 leading-relaxed" data-id="element-450">
                <span className="font-bold text-forest-dark" data-id="element-451">
                  4 Seasonal Checklists
                </span>{' '}
                — Spring, Summer, Fall, Winter (one page each)
              </p>
            </div>
          </div>
        </div>

        {/* Price Display */}
        <div className="text-center mt-14" data-id="element-452">
          <div className="flex items-center justify-center gap-4 mb-6" data-id="element-453">
            <span className="text-2xl text-gray-400 line-through font-sans" data-id="element-454">
              $47
            </span>
            <span className="text-5xl md:text-6xl font-bold text-forest font-serif" data-id="element-455">
              $6.37
            </span>
          </div>
          <a href={getHotmartUrl()} target="_blank" rel="noopener noreferrer" onClick={trackAddToCart} className="cta-heartbeat inline-block bg-red-600 hover:bg-red-700 text-white text-lg font-bold py-4 px-10 rounded-lg shadow-lg transition-colors duration-200" data-id="element-456">
            Get Instant Access — $6.37
          </a>
        </div>
      </div>

      {/* Section Divider */}
      <div className="section-divider mt-16" data-id="element-457" />
    </section>;
}